import { computed } from 'vue';

import type { ICrmResolveByPhoneContact } from '@/openapi-clients-generated/suite';

import { ContactResolution } from '@/modules/crm/exports/contact-resolution';
import { DataCache } from '@/shared/data/cache';

/**
 * Thin layer over `ContactResolution` for Talk: a number *we* own — a tenant extension or a tenant
 * phone number — names itself, so it must win over a CRM contact match and must never be handed to
 * the CRM resolver at all (it has no business knowing about extensions, and a resolve-by-phone call
 * for a number we already know the answer to is just wasted work). A number we *used* to own is the
 * exception, and gets the opposite treatment — see the tiers below. Every Talk surface that names a
 * party should go through here rather than `ContactResolution` directly.
 */

interface OwnedNumber {
    /** Our own name for the number, if it has one. */
    name?: string;
    /** True for a soft-deleted (`deletedAt` set) extension/number — see the tiers below. */
    retired: boolean;
}

// `computed`, not a plain assembly on every call, so this stays lazy: merely IMPORTING this module
// must not kick off a talkExtensions/talkNumbers fetch. Reading `isInternalNumber`/etc. very much
// does trigger the fetch — `.value` reads `DataCache.getRef`, which starts the load on first read —
// but nothing forces that read just by having the module in the dependency graph.
//
// `talkExtensions`/`talkNumbers` include soft-deleted rows (a departed employee's extension, a DID
// the tenant released back to the carrier), so a plain last-wins `keyBy` can't tell "this is still
// ours" from "this used to be ours". Built retired-rows-first, active-rows-second, so an active row
// always overwrites a retired row claiming the same extension number / re-acquired DID.
const ownedByNumber = computed(() => {
    const extensions = DataCache.getRef('talkExtensions').value ?? [];
    const numbers = DataCache.getRef('talkNumbers').value ?? [];
    const rows = [
        ...extensions.map(e => ({ key: e.extension, name: e.displayName || e.name || undefined, deletedAt: e.deletedAt })),
        ...numbers.map(n => ({ key: n.phoneNumber, name: n.description || undefined, deletedAt: n.deletedAt }))
    ];

    const map: Record<string, OwnedNumber> = {};
    // Retired first, active second (see comment above) — later assignments win.
    for (const row of [...rows.filter(r => r.deletedAt), ...rows.filter(r => !r.deletedAt)]) {
        map[row.key] = { name: row.name, retired: !!row.deletedAt };
    }
    return map;
});

/**
 * Whether `number` is an ACTIVE tenant-owned extension or tenant phone number — a retired
 * (soft-deleted) row does NOT count. This is what gates the CRM skip: a number we still hold names
 * itself and is never worth a lookup, but a number we've released may since have been reissued by
 * the carrier to an actual CRM contact, so it must still be eligible for resolution.
 */
export function isInternalNumber(number: string | null | undefined): boolean {
    if (!number) return false;
    const owned = ownedByNumber.value[number];
    return !!owned && !owned.retired;
}

/**
 * Our own name for an owned number — active OR retired — so a historical record (e.g. a departed
 * employee's deleted extension) still reads as "Jane Doe" in call history even though the number no
 * longer gates `isInternalNumber`. Undefined for a number we've never owned, or an owned number
 * (active or retired) that was never named.
 *
 * An extension's name is its `displayName`, falling back to its `name` (the dominant convention
 * across the repo — `cdr.vue` used to read bare `.name`; standardising on `displayName || name` here
 * is intentional); a tenant number's name is its `description`.
 */
export function internalNameForNumber(number: string | null | undefined): string | undefined {
    if (!number) return undefined;
    return ownedByNumber.value[number]?.name;
}

/**
 * Display label for a number, matching `ContactResolution.displayLabelForNumber`'s shape (a name,
 * possibly with a "(+N others)" suffix for a CRM match) but with our own ACTIVE numbers taking
 * priority. An active owned number NEVER falls through to CRM: if it has no description the caller
 * falls back to the carrier CNAM / raw digits, which is better than showing an unrelated contact's
 * name for our own extension or number. A RETIRED owned number is the opposite: CRM gets first say
 * (the carrier may have reissued the DID to a real customer), and our stale name is only a fallback
 * for when CRM has nothing.
 */
export function partyLabelForNumber(number: string | null | undefined): string | undefined {
    const owned = number ? ownedByNumber.value[number] : undefined;
    if (owned && !owned.retired) return owned.name;
    return ContactResolution.displayLabelForNumber(number) ?? owned?.name;
}

/**
 * Bare display name for a number — no "(+N others)" suffix — for consumers that use the name rather
 * than show it verbatim: search matching, initials. Same owned-number priority as `partyLabelForNumber`.
 */
export function partyNameForNumber(number: string | null | undefined): string | undefined {
    const owned = number ? ownedByNumber.value[number] : undefined;
    if (owned && !owned.retired) return owned.name;
    return ContactResolution.displayNameForNumber(number) ?? owned?.name;
}

/**
 * The one CRM contact a number resolves to, for surfaces that decorate the name with the contact
 * itself — its avatar, its hover card. Undefined for one of our own ACTIVE numbers (never a CRM
 * contact, whatever CRM says — same priority as `partyLabelForNumber`), for a number not yet looked
 * up or not matched, and for a number several contacts share: a decoration has to belong to exactly
 * one of them, and the "(+N others)" label is all a multi-match gets.
 */
export function uniquePartyContactForNumber(number: string | null | undefined): ICrmResolveByPhoneContact | undefined {
    if (!number) return undefined;
    const owned = ownedByNumber.value[number];
    if (owned && !owned.retired) return undefined;
    const contacts = ContactResolution.contactsForNumber(number);
    return contacts.length === 1 ? contacts[0] : undefined;
}

/**
 * Initials for an avatar placeholder: first + last word initials, the first two letters of a
 * single-word name, '#' when there's no name at all.
 */
export function partyInitials(name: string | null | undefined): string {
    const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
    if (!parts.length) return '#';
    if (parts.length === 1) return parts[0]!.substring(0, 2).toUpperCase();
    return `${parts[0]!.charAt(0)}${parts[parts.length - 1]!.charAt(0)}`.toUpperCase();
}

/**
 * Queue CRM resolution for only the numbers we don't already name ourselves — our own ACTIVE
 * extensions and tenant numbers are never sent to the CRM resolver, but a RETIRED one is (see
 * `isInternalNumber`).
 *
 * Waits for `talkExtensions`/`talkNumbers` to load before filtering: filtering against a cache that
 * hasn't landed yet would call every number external, and then look our own numbers up after all.
 * Uses `DataCache.getRef(...).loadPromise` rather than `DataCache.waitFor` — `waitFor` resolves
 * immediately (`Promise.resolve()`) when the ref already exists with no `loadPromise`, which is
 * exactly the state `getLazyRef` (called by other surfaces, e.g. caller-blocking / talk-* admin
 * helpers, before any read is due) leaves it in; that would let this filter run before the load even
 * starts. `getRef` guarantees a load has been kicked off, so its `loadPromise` is always set.
 */
export function resolvePartyNumbers(numbers: (string | null | undefined)[], opts: { force?: boolean } = {}) {
    void Promise.all([DataCache.getRef('talkExtensions').loadPromise, DataCache.getRef('talkNumbers').loadPromise]).then(() =>
        ContactResolution.resolveNumbers(
            numbers.filter(n => !isInternalNumber(n)),
            opts
        )
    );
}
